import React, { Component, ReactNode } from "react";
import { Icon, MenuItem, Tooltip } from "@material-ui/core";
import styled from "styled-components";

import { IWorkspaceResourceFolder } from "common/interfaces";
import { Gap5 } from "js/react/components/Gap";
import { Thumbnail } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import Thumbnails from "js/core/models/thumbnails";
import { THUMBNAIL_SIZES } from "common/constants";
import getLogger, { LogGroup } from "js/core/logger";
import WorkspaceController from "js/controllers/WorkspaceController";
import { withFirebaseUser } from "js/react/views/Auth/FirebaseUserContext";
import { t } from "js/i18n";
import UpgradeIcon from "js/Components/UpgradeIcon";
import type { SharedSlidesItem } from "js/controllers/SharedSlidesPaneController";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import { getCjkUiFontFamilyCss } from "common/fontConstants";
import { TruncatedTooltip } from "js/editor/PresentationLibrary/components/TruncatedTooltip";
import { getSharedResourceActivityLabel, SharedResourceContextList } from "js/react/views/SharedResources/Components/SharedResourceContextList";
import {
    TeamResourceHiddenIndicator,
    TeamResourceBadgeCluster,
    TEAM_RESOURCE_DESCRIPTION_TOOLTIP_CLASS,
    TeamResourceIconMenu,
    TeamResourceThumbnailActions,
    TeamResourceThumbnailCard,
    TeamResourceThumbnailImageWrap,
    TeamResourceTooltipStyles,
    TeamResourceUsageBadge,
    TeamResourceUsefulnessButton,
} from "js/react/views/SharedResources/Components/TeamResourceThumbnailCard";

const logger = getLogger(LogGroup.THUMBNAIL);

export type SharedSlideThumbnailProps = {
    item: SharedSlidesItem;
    firebaseUser: { uid: string };
    highQuality?: boolean;
    showThemeThumbnails?: boolean;
    selected?: boolean;
    isManageLibrary?: boolean;
    showUpvote?: boolean;
    canUpgradeToManageSharedResources?: boolean;
    folderId?: string;
    onDoubleClick?: (item: SharedSlidesItem) => void;
    onEditSlide?: (item: SharedSlidesItem) => void;
    onDeleteSlide?: (item: SharedSlidesItem) => void;
    onRemoveFromFolder?: (item: SharedSlidesItem) => void;
    onDuplicateSlide?: (item: SharedSlidesItem) => void;
    onMouseDown?: React.MouseEventHandler;
    onMouseUp?: React.MouseEventHandler;
    onContextMenu?: React.MouseEventHandler;
    onVisibleChange?: (isVisible: boolean, item: SharedSlidesItem) => void;
    renderContextMenuItems?: (selection: SharedSlidesItem[]) => ReactNode;
    onMenuClick?: (item: SharedSlidesItem) => void;
    resourceFolders?: IWorkspaceResourceFolder[];
    onNavigateToFolder?: (filter: { folderId: string; subFolderId?: string }) => void;
};

type SharedSlideThumbnailState = {
    url: string;
    isVisible: boolean;
    isHovering: boolean;
    itemInfo: ReactNode | null;
    optimisticLikedByUser: boolean | null;
    optimisticRatedByCount: number | null;
};

const ThumbnailBar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-height: 20px;

    label {
        font-size: 14px;
        line-height: 18px;
        letter-spacing: 0px;
        color: #444;
        font-family: "Source Sans Pro", ${getCjkUiFontFamilyCss()};
        font-weight: 600;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        text-align: left;
        flex: 1;
        min-width: 0;
        text-transform: none;
        margin: 0;
    }

    & > div {
        font-size: 12px;
    }

    .dark-mode & {
        label {
            color: #ddd;
        }
    }
`;

const ThumbnailContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 100%;
    padding: 0;
    margin-top: 4px;
`;

const ThumbnailTitleGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    flex: 1;
`;

const SlideTitleText = styled.div`
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0px;
    color: #444;
    font-family: "Source Sans Pro", ${getCjkUiFontFamilyCss()};
    font-weight: 600;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    text-align: left;
    flex: 0 1 auto;
    min-width: 0;
    text-transform: none;
    margin: 0;

    .dark-mode & {
        color: #ddd;
    }
`;

const ThumbnailRightSlot = styled.div<{ $hidden?: boolean }>`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    margin-left: auto;
    ${props => props.$hidden && "display: none;"}
`;

const InfoIcon = styled.span<{ $active: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    color: ${props => props.$active ? "#11A9E2" : "#CCC"};

    .MuiIcon-root {
        color: inherit;
        font-size: 16px;
        margin: 0;
    }
`;

function SharedSlideLinkBadgeIndicator({
    isTemplate,
    presentationCount,
}: {
    isTemplate: boolean;
    presentationCount: number;
}) {
    return (
        <TeamResourceUsageBadge
            count={presentationCount}
            hidden={isTemplate}
            showTitle
            title={t("Shared Slide")}
            description={t("Only Librarians and Owners can edit. Changes apply everywhere this is used.")}
        />
    );
}

class SharedSlideThumbnailBase extends Component<SharedSlideThumbnailProps, SharedSlideThumbnailState> {
    ref = React.createRef<HTMLDivElement>();
    observer?: IntersectionObserver;
    isUnmounted = false;

    state: SharedSlideThumbnailState = {
        url: "",
        isVisible: false,
        isHovering: false,
        itemInfo: null,
        optimisticLikedByUser: null,
        optimisticRatedByCount: null,
    };

    componentDidMount() {
        void this.loadItemInfo();

        this.observer = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting) {
                this.observer?.disconnect();
                this.setState({ isVisible: true });
            }
        });

        if (this.ref.current) {
            this.observer.observe(this.ref.current);
        }
    }

    componentDidUpdate(prevProps: SharedSlideThumbnailProps, prevState: SharedSlideThumbnailState) {
        const { item, onVisibleChange } = this.props;
        const { isVisible } = this.state;

        if (prevState.isVisible !== isVisible) {
            onVisibleChange?.(isVisible, item);
        }

        if (
            (prevState.isVisible !== isVisible && isVisible) ||
            prevProps.item.id !== item.id ||
            prevProps.item.contentModifiedAt !== item.contentModifiedAt
        ) {
            void this.loadThumbnail()
                .catch(err => logger.error(err, "[SharedSlideThumbnail] loadThumbnail() failed", { id: item.id }));
        }

        if (item.description !== prevProps.item.description) {
            void this.loadItemInfo();
        }

        if (
            prevProps.item.resource.metadata?.ratedByUsers !== item.resource.metadata?.ratedByUsers ||
            prevProps.item.ratedByCount !== item.ratedByCount
        ) {
            this.setState({
                optimisticLikedByUser: null,
                optimisticRatedByCount: null,
            });
        }
    }

    componentWillUnmount() {
        this.isUnmounted = true;
        this.observer?.disconnect();
    }

    async loadThumbnail() {
        const { item, highQuality, showThemeThumbnails = true } = this.props;

        this.setState({ url: "" });

        let url = "";

        const presentation = PresentationEditorController.presentation;
        if (showThemeThumbnails && presentation && !presentation.get("isDummy")) {
            const presentationId = presentation.id;
            const firstSlideModifiedAt = presentation.get("firstSlideModifiedAt");
            url = await Thumbnails.getSignedUrlAndLoad(
                item.id,
                firstSlideModifiedAt,
                presentationId,
                THUMBNAIL_SIZES[highQuality ? "large" : "small"].suffix
            );
        } else {
            url = await Thumbnails.getSignedUrlAndLoad(item.id, item.contentModifiedAt);
        }

        if (!this.isUnmounted) {
            this.setState({ url });
        }
    }

    handleLikeItem = async () => {
        const { item, item: { resource }, firebaseUser } = this.props;
        const { optimisticLikedByUser, optimisticRatedByCount } = this.state;

        let ratedByUsers = resource.metadata?.ratedByUsers ?? [];
        const likedByUser = optimisticLikedByUser ?? ratedByUsers.includes(firebaseUser.uid);
        const ratedByCount = optimisticRatedByCount ?? item.ratedByCount;

        this.setState({
            optimisticLikedByUser: !likedByUser,
            optimisticRatedByCount: Math.max(0, ratedByCount + (likedByUser ? -1 : 1)),
        });

        if (likedByUser) {
            ratedByUsers = ratedByUsers.filter(uid => uid !== firebaseUser.uid);
        } else if (!ratedByUsers.includes(firebaseUser.uid)) {
            ratedByUsers = [...ratedByUsers, firebaseUser.uid];
        }
        const action = resource.metadata?.ratedByUsers?.includes(firebaseUser.uid) ? "unlike" : "like";

        try {
            await WorkspaceController.updateWorkspaceResourceRating({
                resourceId: resource.resourceId,
                resourceType: resource.resourceType,
                action
            });
        } catch (err) {
            this.setState({
                optimisticLikedByUser: null,
                optimisticRatedByCount: null,
            });
            logger.error(err, "[SharedSlideThumbnail] handleLikeItem() failed", { id: resource.resourceId });
        }
    };

    async loadItemInfo() {
        const { item } = this.props;

        try {
            const itemInfo = await getSharedResourceActivityLabel({
                createdAt: item.createdAt,
                createdBy: item.createdBy,
                modifiedAt: item.contentModifiedAt || item.modifiedAt,
                modifiedBy: item.resource.metadata?.modifiedBy,
            });

            if (this.isUnmounted) {
                return;
            }

            this.setState({
                itemInfo
            });
        } catch (err) {
            logger.error(err, "[SharedSlideThumbnail] loadItemInfo() failed", { id: item.id });
        }
    }

    render() {
        const {
            item,
            onDoubleClick,
            selected,
            isManageLibrary,
            canUpgradeToManageSharedResources,
            folderId,
            onEditSlide,
            onDeleteSlide,
            onRemoveFromFolder,
            onDuplicateSlide,
            onMouseDown,
            onMouseUp,
            onContextMenu,
            renderContextMenuItems,
            onMenuClick,
            firebaseUser,
            resourceFolders = [],
            onNavigateToFolder,
            showUpvote,
        } = this.props;
        const { url, itemInfo, isHovering, isVisible, optimisticLikedByUser, optimisticRatedByCount } = this.state;

        const clickProps = isManageLibrary
            ? { onDoubleClick }
            : {
                onDoubleClick,
                ...(onMouseDown ? { onMouseDown } : { onClick: onDoubleClick }),
            };

        const { isTemplate } = item;
        const ratedByUsers = item.resource.metadata?.ratedByUsers ?? [];
        const likedByUser = optimisticLikedByUser ?? ratedByUsers.includes(firebaseUser.uid);
        const ratedByCount = optimisticRatedByCount ?? item.ratedByCount;
        const canUpvote = isManageLibrary || showUpvote;
        const usefulnessActiveLabel = t("You upvoted this as useful. Click to remove your upvote.");
        const usefulnessInactiveLabel = t("Upvote to let your team know this is useful.");
        const hoverActions = isHovering && isManageLibrary && (
            <TeamResourceThumbnailActions
                onClickCapture={() => onMenuClick?.(item)}
                onMouseDown={e => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
                onMouseUp={e => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
                onDoubleClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
                onClick={e => {
                    e.stopPropagation();
                }}
            >
                <TeamResourceIconMenu handleCloseMenu={() => this.setState({ isHovering: false })} fontSize="medium">
                    {renderContextMenuItems?.([item]) ?? [
                        ...(canUpgradeToManageSharedResources ? [
                            <MenuItem key="edit" disabled divider><UpgradeIcon /><Gap5 />{t("Edit Slide...")}</MenuItem>,
                            <MenuItem key="properties" disabled divider><UpgradeIcon /><Gap5 />{t("View Properties...")}</MenuItem>,
                            <MenuItem key="duplicate" disabled divider><UpgradeIcon /><Gap5 />{t("Duplicate")}</MenuItem>,
                        ] : [
                            <MenuItem key="edit" divider onClick={() => onEditSlide?.(item)}><Icon>edit</Icon>{t("Edit Slide...")}</MenuItem>,
                            <MenuItem key="properties" divider onClick={() => onDoubleClick?.(item)}><Icon>label</Icon>{t("View Properties...")}</MenuItem>,
                            <MenuItem key="duplicate" divider onClick={() => onDuplicateSlide?.(item)}><Icon>file_copy</Icon>{t("Duplicate")}</MenuItem>,
                        ]),
                        ...(folderId && onRemoveFromFolder ? [
                            <MenuItem key="remove-from-folder" divider onClick={() => onRemoveFromFolder(item)}>
                                <Icon>remove_circle</Icon>{t("Remove from Folder")}
                            </MenuItem>
                        ] : []),
                        <MenuItem key="delete" onClick={() => onDeleteSlide?.(item)}><Icon>delete</Icon>{t("Delete")}</MenuItem>,
                    ]}
                </TeamResourceIconMenu>
            </TeamResourceThumbnailActions>
        );

        return (
            <TeamResourceThumbnailCard
                ref={this.ref}
                data-item-id={item.id}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                selected={selected}
                onContextMenu={onContextMenu}
                onMouseEnter={() => this.setState({ isHovering: true })}
                onMouseLeave={() => this.setState({ isHovering: false })}
                barVariant="custom"
                barContent={(
                    <ThumbnailContent>
                        <ThumbnailBar>
                            <ThumbnailTitleGroup>
                                <TruncatedTooltip title={item.name} enterDelay={500} enterNextDelay={500}>
                                    <SlideTitleText>{item.name}</SlideTitleText>
                                </TruncatedTooltip>
                                <Tooltip
                                    title={(
                                        <div style={{ padding: 10 }}>
                                            {item.description || t("No Description")}
                                        </div>
                                    )}
                                    classes={{ popper: "popper-white rounded-5", tooltip: TEAM_RESOURCE_DESCRIPTION_TOOLTIP_CLASS }}
                                    placement="top"
                                    arrow
                                    interactive
                                >
                                    <InfoIcon
                                        $active={!!item.description}
                                        onMouseDown={event => event.stopPropagation()}
                                        onClick={event => event.stopPropagation()}
                                    >
                                        <Icon className="material-symbols-outlined">info</Icon>
                                    </InfoIcon>
                                </Tooltip>
                            </ThumbnailTitleGroup>
                            <ThumbnailRightSlot $hidden={isTemplate && !canUpvote}>
                                <TeamResourceBadgeCluster>
                                    {canUpvote && (
                                        <TeamResourceUsefulnessButton
                                            active={likedByUser}
                                            count={ratedByCount}
                                            visible={isVisible}
                                            activeLabel={usefulnessActiveLabel}
                                            inactiveLabel={usefulnessInactiveLabel}
                                            onClick={() => void this.handleLikeItem()}
                                            variant="inline"
                                        />
                                    )}
                                    <SharedSlideLinkBadgeIndicator isTemplate={isTemplate} presentationCount={item.presentationCount} />
                                </TeamResourceBadgeCluster>
                            </ThumbnailRightSlot>
                        </ThumbnailBar>
                        <SharedResourceContextList
                            activityLabel={itemInfo}
                            activityTooltip={itemInfo}
                            resource={item.resource}
                            resourceFolders={resourceFolders}
                            onNavigateToFolder={onNavigateToFolder ? ({ folderId, parentFolderId }) => {
                                onNavigateToFolder({
                                    folderId: parentFolderId ?? folderId,
                                    subFolderId: parentFolderId ? folderId : undefined,
                                });
                            } : undefined}
                        />
                        <TeamResourceTooltipStyles />
                    </ThumbnailContent>
                )}
            >
                <TeamResourceThumbnailImageWrap>
                    <Thumbnail
                        readOnly
                        {...clickProps}
                        url={url}
                        showSpinner
                        style={canUpgradeToManageSharedResources ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                        imageStyle={item.isDisabled ? { filter: "brightness(0.7)" } : {}}
                    >
                        {item.isDisabled && (
                            <TeamResourceHiddenIndicator>
                                <Icon>visibility_off</Icon>
                            </TeamResourceHiddenIndicator>
                        )}
                    </Thumbnail>
                    {hoverActions}
                </TeamResourceThumbnailImageWrap>
            </TeamResourceThumbnailCard>
        );
    }
}

export const SharedSlideThumbnail = withFirebaseUser(SharedSlideThumbnailBase);
